import {
  CommunicationKey,
  CommunicationKeys,
  ExtendedTResponseType,
  GlobalAPIService,
} from '@core';
import { FirebaseService } from '@lib';
import { DataBaseNodes } from '@lib/firebase';
import { useSiteStore } from '@stores';
import {
  LanguageItems,
  LanguageItemsTranslations,
  LanguageItemsTranslationsType,
  Site,
} from '@type';

export class GatewayUtils {
  constructor(private readonly sourceService: FirebaseService) {}

  async getPublicIpInformation(): Promise<{
    ipAddress: string;
    countryCode: string;
  }> {
    const responseMain =
      await GlobalAPIService.getPublicIpInformationFromMainService();
    if (responseMain.data?.ip && responseMain.data.country?.iso_code) {
      return {
        ipAddress: responseMain.data.ip,
        countryCode: responseMain.data.country.iso_code,
      };
    }

    const apiUrl = await this.sourceService.readDataOnce<string>(
      DataBaseNodes.IpApi
    );
    if (apiUrl) {
      const response =
        await GlobalAPIService.getPublicIpInformationFromAlternativeService(
          apiUrl
        );
      return {
        ipAddress: response.data?.ipAddress || '',
        countryCode: response.data?.countryCode || 'DE',
      };
    }

    return {
      ipAddress: '',
      countryCode: 'DE',
    };
  }

  isQRCodeActive = async (uuid: string) => {
    return (
      await this.sourceService.readDataOnce<{ isActive: boolean }>(
        `qrCode/${uuid}`
      )
    )?.isActive;
  };

  responseKeysComparator =
    (
      responseKey?: CommunicationKey | CommunicationKey[],
      requestedSite?: Site
    ) =>
    (response: ExtendedTResponseType<unknown>) => {
      const doKeysMatch = Array.isArray(responseKey)
        ? responseKey.some(key => key === response.message)
        : responseKey === response.message;

      const responseSiteUUID = response.siteUuid ?? response.siteUUID;
      const isResponseForAnySite = !responseSiteUUID;
      const isResponseForTheRequestedSite =
        responseSiteUUID === requestedSite?.siteUuid;

      const isResponseForAnySiteOrForRequestedSite =
        isResponseForAnySite || isResponseForTheRequestedSite;

      return doKeysMatch && isResponseForAnySiteOrForRequestedSite;
    };

  static readonly getOnboardingSuccessKeys = (siteUUID: string) => {
    const sites = useSiteStore.getState().sites;
    const keys: CommunicationKey[] = [
      CommunicationKeys.DummyPatient.CreationSuccess,
      CommunicationKeys.HandShake.ShouldRequest,
    ];
    if (sites.some(site => site.siteUuid === siteUUID && site.sharedSecretKey))
      keys.push(CommunicationKeys.Component.Sent);
    return keys;
  };

  static readonly includesLanguage: (lang: string, code: string) => boolean = (
    lang: string,
    code: string
  ) => {
    return lang.includes(code);
  };

  static readonly mapLangsToMYCLangs: (
    lang: string
  ) => LanguageItemsTranslationsType = (lang: string) => {
    for (const key in LanguageItems) {
      const item = LanguageItems[key as keyof typeof LanguageItems];
      if (this.includesLanguage(lang, item.code)) {
        return LanguageItemsTranslations[
          key as keyof typeof LanguageItemsTranslations
        ];
      }
    }
    return LanguageItemsTranslations.English;
  };
}
